import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';

import type { TranslationProgressMap, TranslationProgressEntry } from '../types';
import {
  canonicalizeLanguageName,
  canonicalizeTranslationProgressMap,
  getLanguageCountryCode,
  SUPPORTED_TRANSLATION_FLAG_LANGUAGES,
} from '../utils/languageIdentity';

interface LanguageProgressChipsProps {
  progress?: TranslationProgressMap;
  onSelectLanguage?: (language: string) => void;
  languages?: string[];
  className?: string;
  balanceWrappedRows?: boolean;
}

type ProgressTuple = [string, TranslationProgressEntry];

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 6px;
  padding: 4px 4px;
  overflow-x: auto;
  flex-wrap: nowrap;
  scrollbar-width: thin;
  scroll-padding-left: 4px;
  scroll-padding-right: 4px;
`;

const ChipStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 28px;
`;

const FlagWrapper = styled.button`
  position: relative;
  display: inline-flex;
  width: 26px;
  height: 19px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

const PercentLabel = styled.span`
  font-size: 10px;
  line-height: 1;
  color: var(--text-secondary);
  letter-spacing: 0.2px;
`;

const FlagIcon = styled.span<{
  $status?: 'complete' | 'in_progress' | 'not_started';
  $percentage?: number;
  $isActive?: boolean;
}>`
  display: inline-flex;
  width: 26px;
  height: 19px;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
  opacity: ${props => {
    switch(props.$status) {
      case 'complete': return 1;
      case 'in_progress': return 0.9;
      default: return 0.3;
    }
  }};
  
  filter: ${props => {
    switch(props.$status) {
      case 'complete': return 'none';
      case 'in_progress': return 'none';
      default: return 'grayscale(100%)';
    }
  }};
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    z-index: 10;
  }
  
  box-shadow: ${props => {
    if (props.$status === 'complete') {
      return '0 0 0 2px var(--text-success)';
    }
    if (props.$status === 'in_progress') {
      const percentage = props.$percentage || 0;
      let color = 'var(--text-danger)';
      if (percentage >= 100) color = 'var(--text-success)';
      else if (percentage >= 50) color = 'var(--text-warning)';
      return `0 0 0 2px ${color}`;
    }
    return '0 1px 3px rgba(0, 0, 0, 0.12)';
  }};
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    pointer-events: none;
  }

  /* Flag images */
  &[data-country="NL"] { background-image: url('https://flagcdn.com/w40/nl.png'); }
  &[data-country="US"] { background-image: url('https://flagcdn.com/w40/us.png'); }
  &[data-country="GB"] { background-image: url('https://flagcdn.com/w40/gb.png'); }
  &[data-country="FR"] { background-image: url('https://flagcdn.com/w40/fr.png'); }
  &[data-country="DE"] { background-image: url('https://flagcdn.com/w40/de.png'); }
  &[data-country="IT"] { background-image: url('https://flagcdn.com/w40/it.png'); }
  &[data-country="PT"] { background-image: url('https://flagcdn.com/w40/pt.png'); }
  &[data-country="BR"] { background-image: url('https://flagcdn.com/w40/br.png'); }
  &[data-country="ES"] { background-image: url('https://flagcdn.com/w40/es.png'); }
  &[data-country="MX"] { background-image: url('https://flagcdn.com/w40/mx.png'); }
  &[data-country="JP"] { background-image: url('https://flagcdn.com/w40/jp.png'); }
  &[data-country="KR"] { background-image: url('https://flagcdn.com/w40/kr.png'); }
  &[data-country="TW"] { background-image: url('https://flagcdn.com/w40/tw.png'); }
  &[data-country="CN"] { background-image: url('https://flagcdn.com/w40/cn.png'); }
  &[data-country="RU"] { background-image: url('https://flagcdn.com/w40/ru.png'); }
  &[data-country="TH"] { background-image: url('https://flagcdn.com/w40/th.png'); }
  &[data-country="IN"] { background-image: url('https://flagcdn.com/w40/in.png'); }
  &[data-country="AD"] { background-image: url('https://flagcdn.com/w40/ad.png'); }
  &[data-country="AR"] { background-image: url('https://flagcdn.com/w40/ar.png'); }
  &[data-country="PL"] { background-image: url('https://flagcdn.com/w40/pl.png'); }
  &[data-country="UA"] { background-image: url('https://flagcdn.com/w40/ua.png'); }
  &[data-country="SE"] { background-image: url('https://flagcdn.com/w40/se.png'); }
  &[data-country="NO"] { background-image: url('https://flagcdn.com/w40/no.png'); }
  &[data-country="RS"] { background-image: url('https://flagcdn.com/w40/rs.png'); }
  &[data-country="ZA"] { background-image: url('https://flagcdn.com/w40/za.png'); }
  &[data-country="TR"] { background-image: url('https://flagcdn.com/w40/tr.png'); }
  &[data-country="CZ"] { background-image: url('https://flagcdn.com/w40/cz.png'); }
  &[data-country="DK"] { background-image: url('https://flagcdn.com/w40/dk.png'); }
  &[data-country="BA"] { background-image: url('https://flagcdn.com/w40/ba.png'); }
  &[data-country="HR"] { background-image: url('https://flagcdn.com/w40/hr.png'); }
  &[data-country="FI"] { background-image: url('https://flagcdn.com/w40/fi.png'); }
  &[data-country="IS"] { background-image: url('https://flagcdn.com/w40/is.png'); }
  &[data-country="BG"] { background-image: url('https://flagcdn.com/w40/bg.png'); }

  /* Neutral representation for Hebrew/Yiddish */
  &[data-country="HE"] {
    background-image: none;
    background-color: var(--surface-muted);
    color: var(--text-main);
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  }
`;

const STATUS_ORDER: Record<TranslationProgressEntry['status'], number> = {
  in_progress: 0,
  complete: 1,
  not_started: 2,
};

const sortProgressEntries = (entries: ProgressTuple[]): ProgressTuple[] => {
  return [...entries].sort((a, b) => {
    const statusOrder = STATUS_ORDER[a[1].status] - STATUS_ORDER[b[1].status];
    if (statusOrder !== 0) return statusOrder;
    return b[1].percentage - a[1].percentage;
  });
};

const LanguageProgressChips: React.FC<LanguageProgressChipsProps> = ({
  progress,
  onSelectLanguage,
  languages,
  className,
  balanceWrappedRows = false,
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [balancedColumns, setBalancedColumns] = useState<number | null>(null);
  const normalizedProgress = useMemo(
    () => canonicalizeTranslationProgressMap(progress),
    [progress],
  );
  const entries = useMemo(() => {
    const base = languages && languages.length ? languages.map(language => canonicalizeLanguageName(language)) : SUPPORTED_TRANSLATION_FLAG_LANGUAGES;
    const set = new Set(base);
    if (normalizedProgress) {
      Object.keys(normalizedProgress).forEach(lang => set.add(canonicalizeLanguageName(lang)));
    }
    const allLanguages = Array.from(set);
    const tuples: ProgressTuple[] = allLanguages.map(language => [
      language,
      normalizedProgress?.[language] || {
        status: 'not_started',
        percentage: 0,
        translated_chapters: 0,
        total_chapters: 0,
      },
    ]);
    return sortProgressEntries(tuples);
  }, [normalizedProgress, languages]);

  useEffect(() => {
    if (!balanceWrappedRows) {
      setBalancedColumns(null);
      return;
    }

    const element = rowRef.current;
    if (!element || entries.length <= 1) {
      setBalancedColumns(null);
      return;
    }

    const updateBalancedColumns = () => {
      const children = Array.from(element.children) as HTMLElement[];
      if (!children.length) {
        setBalancedColumns(null);
        return;
      }

      const computedStyle = window.getComputedStyle(element);
      const horizontalPadding =
        (parseFloat(computedStyle.paddingLeft) || 0) +
        (parseFloat(computedStyle.paddingRight) || 0);
      const availableWidth = element.clientWidth - horizontalPadding;
      const gap = parseFloat(computedStyle.columnGap || computedStyle.gap) || 0;
      const chipWidth = Math.max(...children.map(child => child.getBoundingClientRect().width));

      if (!availableWidth || !chipWidth) {
        setBalancedColumns(null);
        return;
      }

      const maxColumns = Math.max(1, Math.floor((availableWidth + gap) / (chipWidth + gap)));
      if (maxColumns >= entries.length) {
        setBalancedColumns(null);
        return;
      }

      const rowCount = Math.ceil(entries.length / maxColumns);
      setBalancedColumns(Math.ceil(entries.length / rowCount));
    };

    updateBalancedColumns();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateBalancedColumns);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateBalancedColumns);
    return () => window.removeEventListener('resize', updateBalancedColumns);
  }, [balanceWrappedRows, entries.length]);

  if (!entries.length) {
    return null;
  }

  const rowStyle = balancedColumns
    ? ({ '--language-progress-balanced-columns': String(balancedColumns) } as React.CSSProperties)
    : undefined;

  return (
    <Row
      ref={rowRef}
      className={className}
      data-balanced-columns={balancedColumns ? 'true' : undefined}
      style={rowStyle}
    >
      {entries.map(([language, entry]) => {
        const percent = Math.min(100, Math.max(0, Math.round(entry.percentage)));
        const total = entry.total_chapters ?? 0;
        const translated = entry.translated_chapters ?? 0;
        const remaining = Math.max(total - translated, 0);
        const tooltip =
          entry.status === 'not_started'
            ? `${language}: Not started`
            : `${language}: ${translated.toLocaleString()} of ${total.toLocaleString()} chapters translated` +
              (remaining > 0 ? ` • ${remaining.toLocaleString()} left` : '');
        const countryCode = getLanguageCountryCode(language);
        return (
          <Tooltip key={language} title={tooltip}>
            <ChipStack>
              <FlagWrapper
                type="button"
                aria-label={`${language} translation flag: ${entry.status.replace('_', ' ')}`}
                onClick={() => onSelectLanguage?.(language)}
              >
                <FlagIcon
                  data-country={countryCode}
                  $status={entry.status}
                  $percentage={entry.percentage}
                >
                  {language === 'Hebrew'
                    ? 'א'
                    : !countryCode
                    ? language.slice(0, 2).toUpperCase()
                    : ''}
                </FlagIcon>
              </FlagWrapper>
              <PercentLabel>{percent}%</PercentLabel>
            </ChipStack>
          </Tooltip>
        );
      })}
    </Row>
  );
};

export default LanguageProgressChips;
